/**
 * Pure helpers shared by the laboratory cards and trend graph.
 */

import type { LabTrendPoint } from '../../../types';
import { formatDisplayDate } from '../../../utils/displayDate';

export interface ReferenceRange {
  minimum: number | null;
  maximum: number | null;
}

/** Abbreviated month names for compact time-axis labels. */
const MONTH_ABBR = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

/** A single time-axis tick positioned as a 0..1 fraction of the x range. */
export interface TimeAxisTick {
  position: number;
  label: string;
}

/** A nice, rounded numeric axis with its bounds, tick values, and tick step. */
export interface NumericAxis {
  ticks: number[];
  lower: number;
  upper: number;
  step: number;
}

/**
 * Convert an ISO-ish date string to a millisecond timestamp.
 *
 * @param date - Date string from a lab measurement (e.g. `2026-07-01`).
 * @returns The timestamp in milliseconds, or NaN when unparseable.
 */
export function toTimestamp(date: string): number {
  return new Date(date).getTime();
}

/**
 * Round a range to a "nice" number (1, 2, 5 × 10ⁿ) for clean axis ticks.
 *
 * @param range - The raw span to round.
 * @param round - When true, round to the nearest nice number; otherwise ceil.
 * @returns The nice number closest to the requested span.
 */
function niceNum(range: number, round: boolean): number {
  const exponent = Math.floor(Math.log10(range));
  const fraction = range / 10 ** exponent;
  let niceFraction: number;
  if (round) {
    niceFraction = fraction < 1.5 ? 1 : fraction < 3 ? 2 : fraction < 7 ? 5 : 10;
  } else {
    niceFraction = fraction <= 1 ? 1 : fraction <= 2 ? 2 : fraction <= 5 ? 5 : 10;
  }
  return niceFraction * 10 ** exponent;
}

/**
 * Build evenly spaced, human-friendly axis ticks (integers where possible).
 *
 * @param minimum - Lowest value the axis must include.
 * @param maximum - Highest value the axis must include.
 * @param targetCount - Approximate number of ticks desired.
 * @returns Rounded lower/upper bounds and the tick values between them.
 */
export function getNiceTicks(
  minimum: number,
  maximum: number,
  targetCount = 6,
): NumericAxis {
  if (!Number.isFinite(minimum) || !Number.isFinite(maximum) || maximum <= minimum) {
    const base = Number.isFinite(minimum) ? minimum : 0;
    return { ticks: [base - 1, base, base + 1], lower: base - 1, upper: base + 1, step: 1 };
  }
  const step = niceNum((maximum - minimum) / (targetCount - 1), true);
  const lower = Math.floor(minimum / step) * step;
  const upper = Math.ceil(maximum / step) * step;
  const ticks: number[] = [];
  for (let value = lower; value <= upper + step * 0.5; value += step) {
    ticks.push(Number(value.toFixed(6)));
  }
  return { ticks, lower, upper, step };
}

/** Milliseconds in one day. */
const DAY_MS = 86_400_000;

/** Human-friendly day-step choices for short (sub-quarter) spans. */
const NICE_DAY_STEPS = [1, 2, 3, 7, 14, 30];

/**
 * Format a timestamp as a compact month/year axis label (e.g. `Nov '23`).
 *
 * @param timestamp - Measurement timestamp in milliseconds.
 * @returns The abbreviated month and two-digit year label.
 */
export function formatAxisMonthLabel(timestamp: number): string {
  const at = new Date(timestamp);
  return `${MONTH_ABBR[at.getUTCMonth()]} '${String(at.getUTCFullYear()).slice(-2)}`;
}

/**
 * Format a timestamp as a compact month/day axis label (e.g. `Jul 15`).
 *
 * @param timestamp - Measurement timestamp in milliseconds.
 * @returns The abbreviated month and day-of-month label.
 */
export function formatAxisDayLabel(timestamp: number): string {
  const at = new Date(timestamp);
  return `${MONTH_ABBR[at.getUTCMonth()]} ${at.getUTCDate()}`;
}

/**
 * Build evenly spaced time-axis ticks that adapt to the series span.
 *
 * Sub-month spans step in days (so short series keep real date resolution),
 * larger spans step in months/quarters/years, and the result is always
 * decimated down to `maxTicks` so labels never overlap. Same-day (or empty)
 * spans return no ticks; the caller renders a single centered date label.
 *
 * @param minimumTs - Earliest measurement timestamp (ms).
 * @param maximumTs - Latest measurement timestamp (ms).
 * @param maxTicks - Hard upper bound on how many ticks are returned.
 * @returns Ordered ticks with a 0..1 position and a compact label.
 */
export function getTimeAxisTicks(
  minimumTs: number,
  maximumTs: number,
  maxTicks = 6,
): TimeAxisTick[] {
  if (!Number.isFinite(minimumTs) || !Number.isFinite(maximumTs) || maximumTs <= minimumTs) {
    return [];
  }
  const start = new Date(minimumTs);
  const monthsSpan =
    (new Date(maximumTs).getUTCFullYear() - start.getUTCFullYear()) * 12 +
    (new Date(maximumTs).getUTCMonth() - start.getUTCMonth());
  const span = maximumTs - minimumTs;

  let ticks: TimeAxisTick[] = [];

  if (monthsSpan <= 3) {
    // Short (sub-quarter) span: label individual dates so a windowed view reads
    // as day-level dates rather than month names.
    const daySpan = Math.max(Math.round(span / DAY_MS), 1);
    const rawStep = daySpan / Math.max(maxTicks - 1, 1);
    const dayStep = NICE_DAY_STEPS.find((step) => step >= rawStep) ?? Math.ceil(rawStep);
    let cursor = Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate());
    while (cursor <= maximumTs) {
      if (cursor >= minimumTs) {
        ticks.push({ position: (cursor - minimumTs) / span, label: formatAxisDayLabel(cursor) });
      }
      cursor += dayStep * DAY_MS;
    }
  } else {
    const stepMonths =
      monthsSpan <= maxTicks ? 1 : monthsSpan <= maxTicks * 3 ? 3 : monthsSpan <= maxTicks * 6 ? 6 : 12;
    const year = start.getUTCFullYear();
    let month = Math.ceil(start.getUTCMonth() / stepMonths) * stepMonths;
    let cursor = Date.UTC(year, month, 1);
    while (cursor <= maximumTs) {
      if (cursor >= minimumTs) {
        ticks.push({ position: (cursor - minimumTs) / span, label: formatAxisMonthLabel(cursor) });
      }
      month += stepMonths;
      cursor = Date.UTC(year, month, 1);
    }
  }

  // Enforce the tick budget: decimate uniformly so labels never overlap.
  if (ticks.length > maxTicks) {
    const stride = Math.ceil(ticks.length / maxTicks);
    ticks = ticks.filter((_, index) => index % stride === 0);
  }

  if (ticks.length === 0) {
    ticks.push({ position: 0.5, label: formatAxisMonthLabel(minimumTs) });
  }
  return ticks;
}

/**
 * Parse common laboratory reference-range text into numeric bounds.
 *
 * @param referenceRange - Range such as `4.5-11`, `<=3`, or `>=5`.
 * @returns Numeric lower and upper bounds when available.
 */
export function parseReferenceRange(referenceRange: string): ReferenceRange {
  const range = referenceRange.match(/([\d.]+)\s*-\s*([\d.]+)/);
  if (range) {
    return { minimum: Number(range[1]), maximum: Number(range[2]) };
  }
  const maximum = referenceRange.match(/(?:<|≤)\s*=?\s*([\d.]+)/);
  if (maximum) {
    return { minimum: null, maximum: Number(maximum[1]) };
  }
  const minimum = referenceRange.match(/(?:>|≥)\s*=?\s*([\d.]+)/);
  return {
    minimum: minimum ? Number(minimum[1]) : null,
    maximum: null,
  };
}

/** Clinical classification of a measurement against its reference range. */
export type RangeStatus = 'low' | 'normal' | 'high' | 'unknown';

/**
 * Classify a measurement against its reference range.
 *
 * @param value - The measured value.
 * @param range - Parsed lower/upper reference bounds.
 * @returns `low`/`high` when out of range, `normal` when inside, `unknown` when no range.
 */
export function classifyValue(value: number, range: ReferenceRange): RangeStatus {
  if (range.minimum === null && range.maximum === null) return 'unknown';
  if (range.minimum !== null && value < range.minimum) return 'low';
  if (range.maximum !== null && value > range.maximum) return 'high';
  return 'normal';
}

/**
 * Build a short human-readable clinical status label for a tooltip.
 *
 * @param status - Classification from {@link classifyValue}.
 * @param range - Parsed lower/upper reference bounds.
 * @param unit - Measurement unit (e.g. `g/dL`).
 * @returns A label such as `Low (below 6 g/dL)`, `Normal`, or an empty string.
 */
export function statusLabel(status: RangeStatus, range: ReferenceRange, unit: string): string {
  const suffix = unit ? ` ${unit}` : '';
  if (status === 'low' && range.minimum !== null) return `Low (below ${range.minimum}${suffix})`;
  if (status === 'high' && range.maximum !== null) return `High (above ${range.maximum}${suffix})`;
  if (status === 'normal') return 'Normal';
  return '';
}

/**
 * Build a straight-segment (polyline) SVG path through chronological lab
 * measurements.
 *
 * Consecutive readings are joined with straight lines (no interpolation), so
 * the plotted line reflects only the recorded values.
 *
 * @param points - Chronological trend measurements.
 * @param xScale - Converts a point index to an SVG x coordinate.
 * @param yScale - Converts a measurement to an SVG y coordinate.
 * @returns SVG path data connecting each reading with a straight line.
 */
export function buildLinePath(
  points: LabTrendPoint[],
  xScale: (index: number) => number,
  yScale: (value: number) => number,
): string {
  if (points.length === 0) return '';
  return points
    .map((point, index) => `${index === 0 ? 'M' : 'L'} ${xScale(index)},${yScale(point.value)}`)
    .join(' ');
}

/** Default visible span, in months, when a marker is first shown. */
export const DEFAULT_RANGE_MONTHS = 1;

/** An inclusive date range expressed as millisecond timestamps. */
export interface DateRange {
  /** Inclusive lower bound (ms). */
  startTs: number;
  /** Inclusive upper bound (ms). */
  endTs: number;
}

/**
 * Shift a timestamp by a whole number of calendar months in UTC.
 *
 * @param timestamp - Base timestamp in milliseconds.
 * @param deltaMonths - Months to add (negative to step back).
 * @returns The shifted timestamp in milliseconds.
 */
function shiftMonthsUtc(timestamp: number, deltaMonths: number): number {
  const date = new Date(timestamp);
  return Date.UTC(
    date.getUTCFullYear(),
    date.getUTCMonth() + deltaMonths,
    date.getUTCDate(),
    date.getUTCHours(),
    date.getUTCMinutes(),
    date.getUTCSeconds(),
    date.getUTCMilliseconds(),
  );
}

/**
 * Convert a UTC timestamp into a local `Date` pinned to the same calendar day.
 *
 * Lab readings are parsed as UTC (see {@link toTimestamp}); the calendar works
 * in local time. This bridges the two so the day the clinician sees plotted is
 * the day the calendar pre-selects.
 *
 * @param timestamp - Timestamp in milliseconds (UTC midnight of a reading).
 * @returns A local `Date` at midnight of the equivalent calendar day.
 */
function utcTimestampToLocalDate(timestamp: number): Date {
  const at = new Date(timestamp);
  return new Date(at.getUTCFullYear(), at.getUTCMonth(), at.getUTCDate());
}

/**
 * Find the earliest and latest reading timestamps in a history.
 *
 * @param points - Chronological trend measurements.
 * @returns The data extent, or null when no reading has a valid date.
 */
export function getDataExtent(points: LabTrendPoint[]): { earliestTs: number; latestTs: number } | null {
  const valid = points.map((point) => toTimestamp(point.date)).filter((value) => Number.isFinite(value));
  if (valid.length === 0) return null;
  return { earliestTs: Math.min(...valid), latestTs: Math.max(...valid) };
}

/**
 * Compute the inclusive local-date bounds a user may pick from — the first and
 * last reading days. The picker greys out everything outside this span.
 *
 * @param points - Chronological trend measurements.
 * @returns Local `min`/`max` selectable days, or null when there is no data.
 */
export function getSelectableBounds(points: LabTrendPoint[]): { min: Date; max: Date } | null {
  const extent = getDataExtent(points);
  if (!extent) return null;
  return {
    min: utcTimestampToLocalDate(extent.earliestTs),
    max: utcTimestampToLocalDate(extent.latestTs),
  };
}

/**
 * Compute the default visible range: the most recent `months` of data anchored
 * to the latest reading (clamped so it never starts before the first reading).
 *
 * @param points - Chronological trend measurements.
 * @param months - How many months back from the latest reading to include.
 * @returns Local start/end `Date`s for the picker, or null when there is no data.
 */
export function getDefaultDateRange(
  points: LabTrendPoint[],
  months = DEFAULT_RANGE_MONTHS,
): { start: Date; end: Date } | null {
  const extent = getDataExtent(points);
  if (!extent) return null;
  const startCandidateTs = Math.max(shiftMonthsUtc(extent.latestTs, -months), extent.earliestTs);
  return {
    start: utcTimestampToLocalDate(startCandidateTs),
    end: utcTimestampToLocalDate(extent.latestTs),
  };
}

/**
 * Convert a pair of local calendar `Date`s into an inclusive UTC-day range that
 * lines up with reading timestamps ({@link toTimestamp}).
 *
 * @param start - Selected start day (local).
 * @param end - Selected end day (local).
 * @returns Start-of-start-day and end-of-end-day timestamps (ms).
 */
export function rangeToTimestamps(start: Date, end: Date): DateRange {
  const startTs = Date.UTC(start.getFullYear(), start.getMonth(), start.getDate());
  const endTs = Date.UTC(end.getFullYear(), end.getMonth(), end.getDate()) + DAY_MS - 1;
  return { startTs, endTs };
}

/**
 * Select the history indices whose readings fall inside an inclusive range.
 *
 * @param points - Chronological trend measurements.
 * @param range - Inclusive timestamp bounds from {@link rangeToTimestamps}.
 * @returns Ascending indices of readings within the range.
 */
export function filterHistoryIndicesByRange(points: LabTrendPoint[], range: DateRange): number[] {
  const indices: number[] = [];
  points.forEach((point, index) => {
    const ts = toTimestamp(point.date);
    if (Number.isFinite(ts) && ts >= range.startTs && ts <= range.endTs) {
      indices.push(index);
    }
  });
  return indices;
}

/**
 * Format a selected range for the picker trigger label.
 *
 * @param start - Range start day (local).
 * @param end - Range end day (local).
 * @returns A compact label such as `Jun 24 – Jul 24, 2026`.
 */
export function formatRangeLabel(start: Date, end: Date): string {
  const sameYear = start.getFullYear() === end.getFullYear();
  const startText = `${MONTH_ABBR[start.getMonth()]} ${start.getDate()}${
    sameYear ? '' : `, ${start.getFullYear()}`
  }`;
  const endText = `${MONTH_ABBR[end.getMonth()]} ${end.getDate()}, ${end.getFullYear()}`;
  return `${startText} – ${endText}`;
}

/**
 * Identify the measurements worth marking permanently on a de-cluttered chart.
 *
 * @param points - Chronological trend measurements.
 * @returns Indices of the latest, minimum, and maximum measurements.
 */
export function getKeyPointIndices(points: LabTrendPoint[]): Set<number> {
  if (points.length === 0) return new Set();
  let minIndex = 0;
  let maxIndex = 0;
  points.forEach((point, index) => {
    if (point.value < points[minIndex].value) minIndex = index;
    if (point.value > points[maxIndex].value) maxIndex = index;
  });
  return new Set([minIndex, maxIndex, points.length - 1]);
}

/**
 * Describe a set of readings' direction and current range state.
 *
 * Operates on whatever slice of history it is given (e.g. the active time
 * window), so the bullets stay consistent with what the chart is showing.
 *
 * @param points - Chronological readings to summarize (oldest first).
 * @param label - Analyte label (e.g. `AST`).
 * @param unit - Measurement unit (e.g. `IU/L`).
 * @param latestStatus - Human status word for the latest reading (e.g. `normal`).
 * @returns Short clinical trend summary bullets.
 */
export function buildTrendSummary(
  points: LabTrendPoint[],
  label: string,
  unit: string,
  latestStatus: string,
): string[] {
  if (points.length === 0) return [];
  if (points.length === 1) {
    const only = points[0];
    return [
      `Only one measurement recorded: ${only.value} ${unit} on ${formatDisplayDate(only.date)}.`,
      `A trend line needs at least two readings; this result is classified as ${latestStatus}.`,
    ];
  }
  const first = points[0].value;
  const latest = points[points.length - 1].value;
  const change = latest - first;
  const percent = first === 0 ? null : (change / first) * 100;
  const direction = change > 0 ? 'increased' : change < 0 ? 'decreased' : 'remained stable';
  const changeText =
    change === 0
      ? `${label} has ${direction} at ${latest} ${unit}.`
      : `${label} has ${direction} by ${Math.abs(change).toFixed(1)} ${unit}${
          percent === null ? '' : ` (${Math.abs(percent).toFixed(1)}%)`
        } over ${points.length} measurements.`;
  return [changeText, `The latest result is classified as ${latestStatus}.`];
}

/**
 * Convert a result status to a safe CSS modifier.
 *
 * @param status - Laboratory status label.
 * @returns Lowercase kebab-case class modifier.
 */
export function statusModifier(status: string): string {
  return status.trim().toLowerCase().replace(/\s+/g, '-');
}
